/**
 * @voltxampere/web — Component live measurements (Sprint 18 D4, A4).
 *
 * Bridges the inspector panel to the waveform sample buffers so a
 * selected component's live readings (V_RMS, V_peak, V_pp, …)
 * appear next to the formula. The mockup shows an 8-row table
 * inside the inspector — this module supplies the values.
 *
 * Strategy:
 *
 * 1. For the chosen component, find its first non-ground terminal.
 * 2. Look up the channel currently probing that net.
 * 3. Snapshot the channel's sample buffer; if empty, return null.
 * 4. Compute V_RMS, V_peak, V_pp, V_avg, V_max, V_min, frequency,
 *    and period via `@voltxampere/ui/waveform/measurements`.
 *
 * Returns `null` (em-dash sentinel at the call site) when no
 * channel is bound or the buffer is empty — Kural 9, no fake
 * fallback values.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import type { ComponentInstance, Circuit } from "@voltxampere/schema";
import { computeMeasurement, type MeasurementId } from "@voltxampere/ui/waveform";

import { channels } from "./channels.js";
import { probeBindings } from "./probes.js";

export interface ComponentMeasurements {
  readonly netId: string;
  readonly channelId: string;
  readonly vrms: number | null;
  readonly vpeak: number | null;
  readonly vpp: number | null;
  readonly vavg: number | null;
  readonly vmax: number | null;
  readonly vmin: number | null;
  readonly frequency: number | null;
  readonly period: number | null;
}

const KINDS: readonly MeasurementId[] = [
  "vrms",
  "vmax",
  "vmin",
  "vpp",
  "vavg",
  "frequency",
  "period",
] as const;

/**
 * Pick the representative net for a component. Skips ground nets
 * so the inspector reports a meaningful node rather than the
 * KCL-pinned 0 V reference.
 *
 * Sprint 66.1 hotfix (KOL-C-INSPECTOR-1, ADR-0101): when a probe
 * binding map is provided, prefer a non-ground terminal that is
 * already probed over the first-non-ground default. Without this
 * preference, the inspector frequently picks a terminal (e.g. R₁'s
 * `vcc`-side) that no channel is listening to — even though another
 * of the component's terminals (e.g. R₁'s `mid`-side) IS bound to
 * CH1 by the Sprint 19 boot auto-bind. The downstream lookup misses
 * and the inspector surfaces "Canlı veri yok" despite the engine
 * having freshly populated the buffer. Letting the inspector follow
 * the probe is the smaller of the two possible fixes (the larger
 * would be to auto-bind every terminal of every component, which
 * ADR-0036 explicitly forbids).
 */
export function pickInspectorNet(
  component: ComponentInstance,
  circuit: Circuit | null,
  probedNets?: ReadonlySet<string>,
): string | null {
  if (component.terminals.length === 0) return null;
  const groundIds =
    circuit === null
      ? new Set<string>()
      : new Set(circuit.nets.filter((n) => n.isGround).map((n) => n.id));
  if (probedNets !== undefined && probedNets.size > 0) {
    for (const t of component.terminals) {
      if (probedNets.has(t.netId) && !groundIds.has(t.netId)) return t.netId;
    }
  }
  for (const t of component.terminals) {
    if (!groundIds.has(t.netId)) return t.netId;
  }
  return component.terminals[0]?.netId ?? null;
}

/**
 * Estimate the sample rate from a buffer snapshot. Falls back to a
 * conservative 1 kHz when there is only one sample (no Δt to
 * measure) — measurements that need a real rate (`frequency`,
 * `period`) tolerate the fallback by returning `null` for
 * insufficient data.
 */
function estimateSampleRate(times: Float64Array): number {
  if (times.length < 2) return 1_000;
  const last = times[times.length - 1] ?? 0;
  const first = times[0] ?? 0;
  const dur = last - first;
  if (!Number.isFinite(dur) || dur <= 0) return 1_000;
  return (times.length - 1) / dur;
}

export function computeComponentMeasurements(
  component: ComponentInstance,
  circuit: Circuit | null,
): ComponentMeasurements | null {
  // Sprint 66.1 — pass the current probe-binding keys so
  // `pickInspectorNet` prefers a terminal that's actually bound to
  // a channel before falling back to first-non-ground. Closes
  // KOL-C-INSPECTOR-1 / ADR-0101.
  const probedNets = new Set(probeBindings.value.keys());
  const netId = pickInspectorNet(component, circuit, probedNets);
  if (netId === null) return null;
  const channelId = probeBindings.value.get(netId);
  if (channelId === undefined) return null;
  const channel = channels.value.get(channelId);
  if (channel === undefined) return null;
  const snap = channel.buffer.snapshot();
  if (snap.values.length === 0) return null;

  const sampleRate = estimateSampleRate(snap.times);
  const ctx = { times: snap.times };
  const lookup = new Map<MeasurementId, number | null>();
  for (const kind of KINDS) {
    const result = computeMeasurement(kind, snap.values, sampleRate, ctx);
    lookup.set(kind, result.value);
  }
  const vmax = lookup.get("vmax") ?? null;
  const vmin = lookup.get("vmin") ?? null;
  return {
    netId,
    channelId,
    vrms: lookup.get("vrms") ?? null,
    vpeak: vmax !== null && vmin !== null ? Math.max(Math.abs(vmax), Math.abs(vmin)) : null,
    vpp: lookup.get("vpp") ?? null,
    vavg: lookup.get("vavg") ?? null,
    vmax,
    vmin,
    frequency: lookup.get("frequency") ?? null,
    period: lookup.get("period") ?? null,
  };
}
